import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Alert, Badge, Button, Form, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { FaArrowLeft, FaEdit, FaSave } from 'react-icons/fa';
import Wrapper from '../../components/Wrapper';
import CollapsibleSection from '../../components/CollapsibleSection';
import DeviceVersionFields from '../../components/device/DeviceVersionFields';
import { useDeviceDetail } from '../../hooks/useDeviceDetail';
import { resolveVersionIdsFromDropdowns } from '../../hooks/useDeviceVersionOptions';
import { deviceApi } from '../../services/deviceApi';
import { pickPartnerFieldOfficerId } from '../../utils/deviceVersionUtils';
import { fetchPartners } from '../../store/features/partnersSlice';
import { fetchFieldOfficersByAdminId } from '../../store/features/fieldOfficerByPartnerSlice';
import { selectCurrentRole, selectRoleLoading } from '../../store/features/roleSlice';
import { formatDeviceDate, formatDeviceDateTime } from '../../utils/deviceVersionUtils';

const StatusBadge = ({ status }) => (
  status === false ? <Badge bg="danger">Disabled</Badge> : <Badge bg="success">Enabled</Badge>
);

function DeviceDetail() {
  const { deviceId: rawDeviceId } = useParams();
  const deviceId = decodeURIComponent(rawDeviceId || '');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const role = useSelector(selectCurrentRole);
  const roleLoading = useSelector(selectRoleLoading);
  const { partners, status: partnersStatus } = useSelector((state) => state.partners);
  const { fieldOfficers, status: fieldOfficersStatus } = useSelector((state) => state.fieldOfficerByPartner);
  const { partner: partnerProfile } = useSelector((state) => state.partner);

  const cachedRef = useRef(location.state?.device);
  const isSupport = role === 'SUPPORT';
  const isPartner = (role === 'PARTNER' || role === 'TEAM_MEMBER')
  const canEdit = isSupport || isPartner;

  const {
    device,
    setDevice,
    loading,
    error,
    reloadHistory,
  } = useDeviceDetail(deviceId, {
    cachedDevice: cachedRef.current,
    isSupport,
    roleReady: !roleLoading,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editFormLoading, setEditFormLoading] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing && isSupport && partnersStatus === 'idle') {
      dispatch(fetchPartners({ page: 1, limit: 100 }));
    }
  }, [isEditing, isSupport, partnersStatus, dispatch]);

  useEffect(() => {
    if (isEditing && editForm.partner_id) {
      dispatch(fetchFieldOfficersByAdminId({ admin_id: editForm.partner_id }));
    }
  }, [isEditing, editForm.partner_id, dispatch]);

  useEffect(() => {
    if (!isEditing || fieldOfficersStatus === 'loading') return;
    const validFoId = pickPartnerFieldOfficerId(fieldOfficers, editForm.field_officer_id);
    if (String(validFoId) !== String(editForm.field_officer_id || '')) {
      setEditForm((prev) => ({ ...prev, field_officer_id: validFoId }));
    }
  }, [isEditing, fieldOfficers, fieldOfficersStatus, editForm.field_officer_id]);

  const startEdit = async () => {
    setSaveError('');
    if (isSupport) setEditFormLoading(true);
    setIsEditing(true);

    const partnerId = isPartner
      ? (partnerProfile?.admin_id || device.partner_id || '')
      : (device.partner_id || '');

    if (partnerId) {
      dispatch(fetchFieldOfficersByAdminId({ admin_id: partnerId }));
    }

    const warrantyStart = device.warranty_start_date
      ? String(device.warranty_start_date).split('T')[0]
      : '';
    const warrantyEnd = device.warranty_end_date
      ? String(device.warranty_end_date).split('T')[0]
      : '';

    try {
      const versionIds = isSupport
        ? await resolveVersionIdsFromDropdowns(device)
        : { hardware_id: '', display_software_id: '', device_software_id: '' };
      setEditForm({
        partner_id: partnerId,
        field_officer_id: device.field_officer_id || '',
        warranty_start_date: warrantyStart,
        warranty_end_date: warrantyEnd,
        ...versionIds,
      });
    } catch {
      setEditForm({
        partner_id: partnerId,
        field_officer_id: device.field_officer_id || '',
        warranty_start_date: warrantyStart,
        warranty_end_date: warrantyEnd,
        hardware_id: '',
        display_software_id: '',
        device_software_id: '',
      });
    } finally {
      setEditFormLoading(false);
    }
  };

  const handlePartnerChange = (e) => {
    const partner_id = e.target.value;
    setEditForm((prev) => ({ ...prev, partner_id, field_officer_id: '' }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        partner_id: editForm.partner_id || null,
        field_officer_id: editForm.field_officer_id || null,
        warranty_start_date: editForm.warranty_start_date || null,
        warranty_end_date: editForm.warranty_end_date || null,
      };
      if (isSupport) {
        payload.hardware_id = editForm.hardware_id || null;
        payload.display_software_id = editForm.display_software_id || null;
        payload.device_software_id = editForm.device_software_id || null;
      }
      if (isPartner && partnerProfile?.admin_id) {
        payload.partner_id = partnerProfile.admin_id;
      }

      const result = await deviceApi.updateDeviceAssignment(deviceId, payload);
      setDevice(result.device || device);
      setIsEditing(false);
      await reloadHistory();
    } catch (err) {
      setSaveError(err?.response?.data?.error || err.message || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !device) {
    return (
      <Wrapper>
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2 mb-0">Loading device details...</p>
        </div>
      </Wrapper>
    );
  }

  if ((error && !device) || !device) {
    return (
      <Wrapper>
        <Alert variant="danger">{error || 'Device not found.'}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/device')}>
          <FaArrowLeft className="me-2" />
          Back to Devices
        </Button>
      </Wrapper>
    );
  }

  const status = device.device_status ?? device.status;

  return (
    <Wrapper>
      <div className="reports-container py-4 w-100">
        <div className="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
          <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" size="sm" onClick={() => navigate('/device')}>
              <FaArrowLeft />
            </Button>
            <h1 className="fw-bolder mb-0 text-uppercase">{device.device_id}</h1>
            {status !== undefined && <StatusBadge status={status} />}
          </div>
          {canEdit && !isEditing && (
            <Button variant="outline-dark" size="sm" onClick={startEdit}>
              <FaEdit className="me-2" />
              Edit Device
            </Button>
          )}
        </div>

        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-body">
            {saveError && <Alert variant="danger">{saveError}</Alert>}

            {!isEditing ? (
              <div className="row g-3">
                <div className="col-md-6 col-lg-4">
                  <small className="text-muted d-block">Partner</small>
                  <span className="text-capitalize">{device.partner_name || 'Unassigned'}</span>
                </div>
                <div className="col-md-6 col-lg-4">
                  <small className="text-muted d-block">Field Officer</small>
                  <span className="text-capitalize">{device.field_officer_name || 'Unassigned'}</span>
                </div>
                {isSupport && (
                  <>
                    <div className="col-md-6 col-lg-4">
                      <small className="text-muted d-block">Hardware Version</small>
                      <span className="text-uppercase">{device.hardware_version || 'Not assigned'}</span>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <small className="text-muted d-block">Display Software</small>
                      <span>{device.display_software_version || 'Not assigned'}</span>
                    </div>
                    <div className="col-md-6 col-lg-4">
                      <small className="text-muted d-block">Firmware Software</small>
                      <span>{device.device_software_version || 'Not assigned'}</span>
                    </div>
                  </>
                )}
                <div className="col-md-6 col-lg-4">
                  <small className="text-muted d-block">Warranty Start</small>
                  <span>{formatDeviceDate(device.warranty_start_date)}</span>
                </div>
                <div className="col-md-6 col-lg-4">
                  <small className="text-muted d-block">Warranty End</small>
                  <span>{formatDeviceDate(device.warranty_end_date)}</span>
                </div>
                <div className="col-md-6 col-lg-4">
                  <small className="text-muted d-block">Location</small>
                  <span>
                    {device.latitude != null && device.longitude != null
                      ? `${Number(device.latitude).toFixed(4)}, ${Number(device.longitude).toFixed(4)}`
                      : '—'}
                  </span>
                </div>
                <div className="col-md-6 col-lg-4">
                  <small className="text-muted d-block">Last Updated</small>
                  <span>{formatDeviceDateTime(device.updated_at)}</span>
                </div>
              </div>
            ) : isSupport && editFormLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
                <p className="mt-2 mb-0 text-muted">Loading dropdown options...</p>
              </div>
            ) : (
              <Form onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
                <div className="row g-3">
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Device ID</Form.Label>
                      <Form.Control value={device.device_id} readOnly disabled className="bg-light" />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Partner</Form.Label>
                      {isPartner ? (
                        <Form.Control
                          value={partnerProfile?.entity_name || device.partner_name || 'Current Partner'}
                          readOnly
                          disabled
                          className="bg-light"
                        />
                      ) : (
                        <Form.Select value={editForm.partner_id} onChange={handlePartnerChange}>
                          <option value="">Unassigned</option>
                          {partners.map((p) => (
                            <option key={p.admin_id} value={p.admin_id}>{p.entity_name}</option>
                          ))}
                        </Form.Select>
                      )}
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Field Officer</Form.Label>
                      <Form.Select
                        value={editForm.field_officer_id}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, field_officer_id: e.target.value }))}
                        disabled={!editForm.partner_id || fieldOfficersStatus === 'loading'}
                      >
                        <option value="">
                          {!editForm.partner_id
                            ? 'Select partner first'
                            : fieldOfficersStatus === 'loading'
                              ? 'Loading...'
                              : 'Unassigned'}
                        </option>
                        {fieldOfficers.map((fo) => (
                          <option key={fo.field_officer_id} value={fo.field_officer_id}>{fo.name}</option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Warranty Start</Form.Label>
                      <Form.Control
                        type="date"
                        value={editForm.warranty_start_date || ''}
                        onChange={(e) => {
                          const start = e.target.value;
                          let end = '';
                          if (start) {
                            const d = new Date(start);
                            d.setFullYear(d.getFullYear() + 1);
                            end = d.toISOString().split('T')[0];
                          }
                          setEditForm((prev) => ({ ...prev, warranty_start_date: start, warranty_end_date: end }));
                        }}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-md-6">
                    <Form.Group>
                      <Form.Label>Warranty End <small className="text-muted">(auto: start + 1 year)</small></Form.Label>
                      <Form.Control
                        type="date"
                        value={editForm.warranty_end_date || ''}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, warranty_end_date: e.target.value }))}
                      />
                    </Form.Group>
                  </div>
                </div>

                {isSupport && (
                  <>
                    <hr />
                    <DeviceVersionFields
                      hardwareId={editForm.hardware_id}
                      displaySoftwareId={editForm.display_software_id}
                      deviceSoftwareId={editForm.device_software_id}
                      onChange={(updates) => setEditForm((prev) => ({ ...prev, ...updates }))}
                    />
                  </>
                )}

                <div className="d-flex gap-2 justify-content-end mt-3">
                  <Button variant="secondary" onClick={() => setIsEditing(false)} disabled={saving}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={saving}>
                    {saving ? <Spinner animation="border" size="sm" /> : <><FaSave className="me-2" />Save</>}
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>

        {!isEditing && isSupport && (
          <>
            <CollapsibleSection title="Hardware Version" defaultOpen>
              <p className="mb-0">
                <strong>Current:</strong>{' '}
                <span className="text-uppercase">{device.hardware_version || 'Not assigned'}</span>
              </p>
            </CollapsibleSection>
            <CollapsibleSection title="Software Version" defaultOpen>
              <p className="mb-1"><strong>Display:</strong> {device.display_software_version || 'Not assigned'}</p>
              <p className="mb-0"><strong>Firmware:</strong> {device.device_software_version || 'Not assigned'}</p>
            </CollapsibleSection>
          </>
        )}
      </div>
    </Wrapper>
  );
}

export default DeviceDetail;
